import {
  Box,
  Button,
  Heading,
  HStack,
  Progress,
  Stack,
  StackDivider
} from '@chakra-ui/react'
import formatDistanceToNow from 'date-fns/formatDistanceToNow'
import {useEffect, useState} from 'react'

import {CheckIcon, ExternalLinkIcon} from 'lib/components/icons'
import {ALink} from 'lib/components/link'
import {secondsToHhMmSsString} from 'lib/utils/time'
import {useCurrentRegionId} from 'lib/region/hooks'

import {useActiveTasks, useRemoveTask} from '../hooks/use-activity'
import useViewWorkProduct from '../hooks/use-view-work-product'

// Default paddings shared across components in the task list
const px = 3
const py = 4

const taskIsFinished = (t: CL.Task) => t.state === 'DONE' || t.state === 'ERROR'

function getColor(task: CL.Task): string {
  switch (task.state) {
    case 'ACTIVE':
      return 'green.500'
    case 'ERROR':
      return 'red.500'
    case 'DONE':
      return 'blue.500'
  }
}

const formatComplete = (secondsComplete: number): string =>
  formatDistanceToNow(Date.now() - secondsComplete * 1_000, {addSuffix: true})

/**
 * Simple component for displaying the time and updating it every second.
 */
function TaskTime({task}: {task: CL.Task}) {
  const {secondsActive, secondsComplete, state} = task
  const [taskState, setTaskState] = useState(task.state)
  const [taskSeconds, setTaskSeconds] = useState(
    taskState === 'ACTIVE' ? secondsActive : secondsComplete
  )

  // Always increment by 1 second
  useEffect(() => {
    const id = setInterval(() => setTaskSeconds((t) => t + 1), 1_000)
    return () => clearInterval(id)
  }, [])

  // Change the base time if the state changes
  useEffect(() => {
    if (taskState !== state) {
      setTaskState(state)
      setTaskSeconds(secondsComplete)
    }
  }, [secondsComplete, state, taskState])

  return (
    <>
      {taskState === 'ACTIVE'
        ? secondsToHhMmSsString(taskSeconds)
        : formatComplete(taskSeconds)}
    </>
  )
}

function LinkToWorkProduct(p: {task: CL.Task}) {
  const removeTask = useRemoveTask()
  const goToWorkProduct = useViewWorkProduct()
  const text =
    p.task.state === 'ERROR' ? 'View error details' : 'View work product'
  return (
    <Button
      colorScheme={p.task.state === 'ERROR' ? 'red' : 'blue'}
      onClick={async () => {
        await removeTask(p.task.id)
        await goToWorkProduct(p.task)
      }}
      px={3}
      rightIcon={<ExternalLinkIcon />}
      variant='ghost'
    >
      {text}
    </Button>
  )
}

function Task({task}: {task: CL.Task}) {
  const removeTask = useRemoveTask()
  return (
    <Stack id={task.id} pl={px} py={py} position='relative' spacing={1}>
      <HStack justify='space-between'>
        <Heading
          color={getColor(task)}
          overflow='hidden'
          size='sm'
          textOverflow='ellipsis'
          title={task.title}
          whiteSpace='nowrap'
          _hover={{
            whiteSpace: 'normal'
          }}
        >
          {task.title}
        </Heading>
        <HStack spacing={1} pr={1}>
          {taskIsFinished(task) && task.workProduct && (
            <Box>
              <LinkToWorkProduct task={task} />
            </Box>
          )}
          <Button
            colorScheme='blue'
            onClick={() => removeTask(task.id)}
            px={3}
            rightIcon={<CheckIcon />}
            variant='ghost'
            visibility={taskIsFinished(task) ? 'inherit' : 'hidden'}
          >
            Clear
          </Button>
        </HStack>
      </HStack>
      <HStack justify='space-between' pr={px} spacing={6}>
        <Box
          textOverflow='ellipsis'
          whiteSpace='nowrap'
          overflow='hidden'
          _hover={{
            whiteSpace: 'normal'
          }}
        >
          {task.detail}
        </Box>
        <Box fontFamily='mono' opacity={0.6} whiteSpace='nowrap'>
          <TaskTime task={task} />
        </Box>
      </HStack>
      {task.state === 'ACTIVE' && (
        <Box pr={px}>
          <Progress
            colorScheme='green'
            hasStripe
            isAnimated
            mt={2}
            value={task.percentComplete}
          />
        </Box>
      )}
    </Stack>
  )
}

export default function TaskList({limit}: {limit?: number}) {
  const activeTasks = useActiveTasks()
  const regionId = useCurrentRegionId()
  return (
    <Stack divider={<StackDivider />} id='TaskList' spacing={0}>
      {activeTasks.length > 0 ? (
        activeTasks.slice(0, limit).map((task) => (
          <Box key={task.id}>
            <Task task={task} />
          </Box>
        ))
      ) : (
        <Box px={px} py={py}>
          No active tasks.
        </Box>
      )}
      {limit !== undefined && limit < activeTasks.length && (
        <Box px={px} py={py}>
          <ALink to='activity' query={{regionId}}>
            View more tasks →
          </ALink>
        </Box>
      )}
    </Stack>
  )
}
